import { useState } from 'react';
import { calculateAmount } from '../utils/calculator';
import { isWeekend } from '../utils/date';
import { DateTime } from 'luxon';
import { FeeType, Site } from '../../generated/graphql';
import { ZONE } from '../constants';
import { PaymentType } from '../types/paymentType';

const isTimelinePrepaySite = (
  site: Site | null,
  treatAsPrepay = false
) => {
  // Tiered sites are now safe to drive from the timeline (buildTimeline uses
  // PricingTier — see central-park-parking-api/src/sites/sites-timeline.service.ts
  // and shared/utils/pricing-tiers.ts). The previous pricingTiers-length-zero
  // guard was a legacy escape hatch from when the timeline was flat-rate only.
  //
  // treatAsPrepay: the post-payment "no session found" fallback reuses this
  // prepay timeline UI for a POSTPAYMENT site, so it must be timeline-driven
  // too (otherwise +/- never advances the timeline index and the amount stays
  // frozen at $0). getSites builds a timeline for every site, post-pay included.
  return (
    !!site &&
    (site.paymentType === PaymentType.PREPAYMENT || treatAsPrepay) &&
    !!site.timeline &&
    site.timeline.length > 0
  );
};

export const useDuration = (treatAsPrepay = false) => {
  const [duration, setDuration] = useState<number>(0);
  const [reachedMaxDuration, setReachedMaxDuration] = useState<boolean>(false);
  const [timeSteps, setTimeSteps] = useState<number>(0);
  const [timelineIndex, setTimelineIndex] = useState<number>(0);

  const increaseDuration = (
    selectedSite: Site | null,
    selectedFee: FeeType,
    specialTariffSelected: number | null
  ) => {
    if (!selectedSite) {
      return;
    }

    if (
      isTimelinePrepaySite(selectedSite, treatAsPrepay) &&
      selectedFee === FeeType.Hourly
    ) {
      const timeline = selectedSite.timeline ?? [];
      if (timeline.length === 0) {
        return;
      }

      const nextIndex = Math.min(timelineIndex + 1, timeline.length - 1);
      setTimelineIndex(nextIndex);
      // Total session minutes for prepay timeline come from getTotalSessionDurationMinutes
      // in Home (actualDuration), not from duration state.

      setReachedMaxDuration(nextIndex === timeline.length - 1);
      return;
    }

    const currentTimeInAuckland = DateTime.now();
    const newDuration = duration + timeSteps;
    if (selectedSite.hasMaxTime || selectedSite.hasMaxTimeWeekend) {
      const isWeekendNow = isWeekend(currentTimeInAuckland);
      const maxCost = isWeekendNow
        ? selectedSite.maxCostWeekend
        : selectedSite.maxCost;
      const maxTimeHours = isWeekendNow
        ? selectedSite.maxTimeWeekend
        : selectedSite.maxTime;
      const amount = calculateAmount(
        selectedSite,
        selectedFee,
        newDuration,
        reachedMaxDuration,
        specialTariffSelected
      );

      const isWeekendAndHasMaxTimeWeekend =
        isWeekendNow && selectedSite.hasMaxTimeWeekend;
      const isWeekdayAndHasMaxTime = !isWeekendNow && selectedSite.hasMaxTime;
      if (
        amount >= maxCost &&
        (isWeekendAndHasMaxTimeWeekend || isWeekdayAndHasMaxTime)
      ) {
        setDuration(maxTimeHours * 60);
        setReachedMaxDuration(true);
        return;
      }
    }
    setDuration(newDuration);
  };

  const decreaseDuration = (selectedSite: Site | null) => {
    if (!selectedSite) {
      return;
    }

    if (isTimelinePrepaySite(selectedSite, treatAsPrepay)) {
      const timeline = selectedSite.timeline ?? [];
      if (timeline.length === 0) {
        return;
      }

      if (timelineIndex <= 0) {
        setTimelineIndex(0);
        setReachedMaxDuration(false);
        return;
      }

      const nextIndex = timelineIndex - 1;
      setTimelineIndex(nextIndex);
      setReachedMaxDuration(false);
      return;
    }

    if (duration - timeSteps < 0) {
      return;
    }

    if (reachedMaxDuration) {
      setReachedMaxDuration(false);
      setDuration(selectedSite.minimumTime);
    } else {
      setDuration(duration - timeSteps);
    }
  };

  const changeSelectedFee = (
    newFee: FeeType,
    selectedSite: Site | null,
    specialTariffSelected: number | null
  ) => {
    if (
      newFee === FeeType.Hourly &&
      isTimelinePrepaySite(selectedSite, treatAsPrepay)
    ) {
      const timeline = selectedSite?.timeline ?? [];
      if (timeline.length > 0) {
        setTimelineIndex(0);
        setDuration(timeline[0].durationMinutes);
        setReachedMaxDuration(timeline.length === 1);
      } else {
        setDuration(0);
        setReachedMaxDuration(false);
      }
      return;
    }

    if (newFee === FeeType.Hourly && selectedSite) {
      setDuration(
        selectedSite.minimumTime +
          (selectedSite.hasFreeTime ? selectedSite.freeTime : 0)
      );
    } else if (newFee === FeeType.Monthly) {
      const today = DateTime.now();
      const in4Weeks = today.plus({ weeks: 4 }).endOf('day');
      const difference = in4Weeks.diff(today, ['minutes']);
      setDuration(Math.round(difference.minutes));
    } else if (newFee === FeeType.Weekly) {
      const today = DateTime.now();
      const in1Week = today.plus({ weeks: 1 }).endOf('day');
      const difference = in1Week.diff(today, ['minutes']);
      setDuration(Math.round(difference.minutes));
    } else if (
      newFee === FeeType.SpecialTariff &&
      selectedSite &&
      specialTariffSelected
    ) {
      const tariffs = selectedSite.specialTariffPricing ?? [];
      const specialTariff = tariffs.find((x) => x.id === specialTariffSelected);
      if (specialTariff) {
        const today = DateTime.now().setZone(ZONE);
        let limitTime = today;
        if (
          specialTariff.start > specialTariff.end ||
          (specialTariff.untilTime &&
            specialTariff.untilTime < specialTariff.start)
        ) {
          limitTime = today.plus({ days: 1 });
        }
        if (specialTariff.untilTime) {
          limitTime = limitTime.set({
            hour: parseInt(specialTariff.untilTime.split(':')[0]),
            minute: parseInt(specialTariff.untilTime.split(':')[1]),
          });
        } else if (specialTariff.sessionTime) {
          const unit = specialTariff.sessionTimeUnit ?? 'hours';
          limitTime = today.plus({
            [unit.toLocaleLowerCase()]: specialTariff.sessionTime,
          });
        }
        const difference = limitTime.diff(today, ['minutes']);
        setDuration(Math.round(difference.minutes));
      }
    }
  };

  return {
    duration,
    setDuration,
    increaseDuration,
    decreaseDuration,
    setTimeSteps,
    changeSelectedFee,
    reachedMaxDuration,
    setReachedMaxDuration,
    timelineIndex,
    setTimelineIndex,
  };
};
